import React from "react";
import SiteLayout from "@/components/SiteLayout";
import HeroBadge from "@/components/HeroBadge";
import { Lock, FileText, Sparkles } from "lucide-react";

function Page({ icon: Icon, eyebrow, title, accent, children }) {
  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10 pt-16 pb-12 relative">
          <HeroBadge>{eyebrow}</HeroBadge>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl font-bold text-[#0F2540] leading-[1.04]">{title}<span className="text-[#A53A2A]">{accent}</span></h1>
        </div>
      </section>
      <section className="mx-auto max-w-4xl px-6 lg:px-10 pb-16">
        <div className="lux-card rounded-3xl p-8 lg:p-12 space-y-6 text-[#0F2540] leading-relaxed">
          {children}
        </div>
      </section>
    </SiteLayout>
  );
}

const H = ({ children }) => <h3 className="font-display text-2xl font-bold text-[#0F2540] mt-8 mb-2">{children}</h3>;
const H4 = ({ children }) => <h4 className="font-display text-lg font-semibold text-[#1E4F87] mt-5 mb-1">{children}</h4>;
const P = ({ children }) => <p className="text-[#5C6B7A]">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 text-[#5C6B7A] marker:text-[#A53A2A]">{children}</ul>;
const LI = ({ children }) => <li>{children}</li>;
const Callout = ({ children, tone = "info" }) => (
  <div className={`mt-3 rounded-xl p-4 border-l-4 ${
    tone === "warn" ? "bg-[#FFF6E6] border-[#C89B3C] text-[#7A5A1F]"
                    : "bg-[#EAF1FA] border-[#1E4F87] text-[#0F2540]"
  }`}>{children}</div>
);

export function Privacy() {
  return (
    <Page icon={Lock} eyebrow="Privacy Policy" title="How we handle your" accent=" data.">
      <P>We take your privacy seriously. Nirmal Hospital collects only the information required to activate and service your Nirmal Privilege Card — name, contact details, date of birth, and family member details you voluntarily provide during purchase.</P>
      <H>What we collect</H>
      <P>Personal details, plan selection, payment transaction metadata (we never store card or bank data), and basic usage analytics to improve our services.</P>
      <H>How we use it</H>
      <P>To service your membership, coordinate appointments, send updates and respond to requests. We do not sell your data — ever.</P>
      <H>Your rights</H>
      <P>You may request a copy, correction or deletion of your data anytime by writing to <a className="text-[#1E4F87] underline" href="mailto:info@nirmalhospitals.in">info@nirmalhospitals.in</a>.</P>
      <H>Contact</H>
      <P>For privacy questions, write to <a className="text-[#1E4F87] underline" href="mailto:npc@nirmalhospitals.in">npc@nirmalhospitals.in</a> or call <a className="text-[#1E4F87] underline" href="tel:+916357033301">+91-63570-33301</a>.</P>
    </Page>
  );
}

export function Terms() {
  return (
    <Page icon={FileText} eyebrow="Terms & Conditions" title="Card Holder" accent=" Agreement.">
      <Callout>
        <p className="font-semibold">Card Holder Declaration</p>
        <p className="text-sm mt-1">By proceeding with registration and payment for the Nirmal Privilege Card ("NPC"), the applicant/card holder acknowledges, understands, and agrees to the following terms and conditions.</p>
      </Callout>

      <H>1. Nature of Program</H>
      <P>1.1 NPC is a healthcare privilege, wellness, and patient support program offered by Nirmal Group of Hospitals.</P>
      <P>1.2 NPC is <b>NOT</b>:</P>
      <UL>
        <LI>A health insurance policy</LI>
        <LI>A mediclaim policy</LI>
        <LI>A cashless hospitalization scheme</LI>
        <LI>A TPA arrangement</LI>
        <LI>A financial product or guarantee of treatment</LI>
      </UL>
      <P>1.3 The NPC Card only provides access to selected healthcare-related privileges and benefits as defined by hospital policy from time to time.</P>

      <H>2. Eligibility</H>
      <P>2.1 NPC Cards may be issued to eligible individuals, couples, families, organizations, or groups subject to verification and approval by hospital management.</P>
      <P>2.2 The hospital reserves the right to approve, reject, suspend, or cancel any application without assigning any reason.</P>
      <P>2.3 Benefits are applicable only to registered card holders enrolled under the approved card category.</P>

      <H>3. Validity</H>
      <P>3.1 Card validity shall commence from the date of successful registration and payment confirmation.</P>
      <P>3.2 Card pricing, validity, inclusions, exclusions, and benefit structure are subject to revision by hospital management at any time.</P>
      <P>3.3 Amounts once paid are generally non-refundable and non-transferable unless specifically approved by hospital management.</P>

      <H>4. OPD Consultation Benefits</H>
      <P>4.1 Eligible card holders may avail unlimited OPD consultations with selected consultants/specialities during designated OPD timings, subject to hospital scheduling and operational availability.</P>
      <P>4.2 Covered specialities may include:</P>
      <UL>
        <LI>General Medicine</LI>
        <LI>Orthopedics</LI>
        <LI>Gynecology</LI>
        <LI>Pediatrics</LI>
        <LI>General Surgery</LI>
      </UL>
      <P>4.3 Visiting consultants and super-specialists may be available at privileged or concessional consultation rates as determined by hospital policy.</P>
      <P>4.4 Benefits are valid only during regular OPD hours and are not applicable during emergency consultations unless specifically approved.</P>

      <H>5. Diagnostic, Pharmacy &amp; Hospital Benefits</H>
      <P>5.1 Eligible discounts or privileged rates may apply on selected:</P>
      <UL>
        <LI>Pathology investigations</LI>
        <LI>Radiology investigations</LI>
        <LI>Preventive health packages</LI>
        <LI>In-house pharmacy purchases</LI>
        <LI>Hospital billing services</LI>
      </UL>
      <P>5.2 Benefits are applicable only on services provided directly within hospital infrastructure unless otherwise specified.</P>
      <P>5.3 Outsourced services, external vendor services, implants, consumables, blood products, and selected medical items may be excluded.</P>

      <H>6. Exclusions</H>
      <P>NPC benefits are generally <b>NOT</b> applicable on:</P>
      <UL>
        <LI>Emergency services</LI>
        <LI>Emergency admissions</LI>
        <LI>ICU admissions</LI>
        <LI>Implants and stents</LI>
        <LI>Blood products</LI>
        <LI>Government taxes / GST</LI>
        <LI>Outsourced investigations</LI>
        <LI>Promotional or discounted packages</LI>
        <LI>Insurance / TPA / government scheme billed services</LI>
        <LI>Special camps or bundled pricing programs</LI>
        <LI>Any service already under discounted pricing</LI>
      </UL>
      <Callout tone="warn">
        <p className="text-sm">Hospital management reserves the sole discretion to determine applicability of benefits.</p>
      </Callout>

      <H>7. No Guarantee of Treatment or Admission</H>
      <P>7.1 NPC does not guarantee:</P>
      <UL>
        <LI>Admission</LI>
        <LI>Bed availability</LI>
        <LI>ICU availability</LI>
        <LI>Emergency priority</LI>
        <LI>Doctor availability</LI>
        <LI>Immediate consultation</LI>
        <LI>Medical outcome or treatment success</LI>
      </UL>
      <P>7.2 All medical decisions shall remain solely under the professional judgment of treating doctors and hospital protocols.</P>

      <H>8. Non-Transferability</H>
      <P>8.1 The NPC Card is strictly non-transferable.</P>
      <P>8.2 Only registered and verified family members listed under the approved card category may avail benefits.</P>
      <P>8.3 Misrepresentation, impersonation, or misuse may result in immediate termination.</P>

      <H>9. Card Holder Responsibilities</H>
      <P>Card holders agree:</P>
      <UL>
        <LI>To provide accurate information</LI>
        <LI>To comply with hospital policies</LI>
        <LI>To carry valid identification when requested</LI>
        <LI>To avoid misuse or fraudulent utilization of benefits</LI>
      </UL>
      <P>Any misuse, misconduct, abuse, or violation of policy may lead to suspension or cancellation without refund.</P>

      <H>10. Hospital Rights</H>
      <P>10.1 Hospital management reserves the right to:</P>
      <UL>
        <LI>Modify benefits</LI>
        <LI>Revise pricing</LI>
        <LI>Change card structure</LI>
        <LI>Suspend or discontinue the program</LI>
        <LI>Amend policies without prior notice</LI>
      </UL>
      <P>10.2 Decisions of hospital management regarding eligibility, benefit applicability, disputes, or interpretation of terms shall be final and binding.</P>

      <H>11. Limitation of Liability</H>
      <P>11.1 The hospital shall not be liable for:</P>
      <UL>
        <LI>Indirect or consequential losses</LI>
        <LI>Delay in service</LI>
        <LI>Technical system downtime</LI>
        <LI>Non-availability of consultants</LI>
        <LI>Force majeure situations</LI>
        <LI>Third-party failures</LI>
      </UL>
      <P>11.2 Benefits are subject to operational feasibility and hospital policy.</P>

      <H>12. Data Privacy &amp; Consent</H>
      <P>12.1 Card holders consent to storage and usage of personal and medical information for operational, healthcare, communication, audit, and program management purposes.</P>
      <P>12.2 The hospital shall take reasonable measures to maintain confidentiality of card holder information as per applicable laws and internal policy.</P>

      <H>13. Governing Jurisdiction</H>
      <P>All disputes shall be subject to the jurisdiction of courts located in Surat, Gujarat.</P>

      <H>Card Holder Declaration</H>
      <Callout>
        <p className="font-semibold">I hereby confirm that:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2 text-sm">
          <li>I have read and understood the Terms &amp; Conditions of the NPC Program.</li>
          <li>I understand that NPC is not an insurance or Mediclaim product.</li>
          <li>I voluntarily enroll for the NPC Card.</li>
          <li>I agree to comply with all applicable hospital policies and program rules.</li>
        </ul>
      </Callout>

      <p className="text-[11px] text-[#5C6B7A] mt-6 pt-4 border-t border-[#E1E8F0]">For queries, write to <a className="text-[#1E4F87] underline" href="mailto:npc@nirmalhospitals.in">npc@nirmalhospitals.in</a> or call <a className="text-[#1E4F87] underline" href="tel:+916357033301">+91-63570-33301</a>.</p>
    </Page>
  );
}
